// Profile of Nikolai Genov, English. Source of the profile page and of the PDF in this language.
module.exports = {
  headline: 'AI & Data Science Lead • Healthcare, Life Sciences, Finance',
  summary: 'Bioinformatics PhD and hands-on lead combining AI-assisted software engineering with analytics in regulated healthcare, life sciences and finance. Expertise spans LLM/RAG systems, MCP integration, clinical data, five-year banking forecasts and automated Excel reporting. Combines R/Python implementation with team coordination, quality assurance and executive-level workshops; AI Act certified and GCP trained.',
  imageAlt: 'Portrait of Nikolai Genov',
  personal: {
    position: 'Co-Founder and Managing Director of pAIoneer Labs GmbH, Managing Director of Leap Dynamics GmbH',
    location: 'Berlin, Germany',
    languages: 'German and English (fluent), Bulgarian (fluent)',
    programmingLanguages: 'R, Python, SQL/SQLite, SAS, MATLAB',
    certifications: 'AI Act certification, GCP training (2025)',
    publications: 'Peer-reviewed work in Scientific Reports, PLOS Biology, IJMS, Frontiers in Physiology and PLOS ONE'
  },
  education: [
    {
      period: '2021',
      field: 'Doctorate: Bioinformatics / Theoretical Biology',
      institution: '',
      degree: 'Degree: PhD (Dr. rer. nat.)'
    },
    {
      period: '2015',
      field: 'Studies: Bioinformatics',
      institution: 'Freie Universität Berlin',
      degree: 'Degree: M.Sc. Bioinformatics'
    },
    {
      period: '2012',
      field: 'Studies: Bioinformatics',
      institution: 'Freie Universität Berlin',
      degree: 'Degree: B.Sc. Bioinformatics'
    }
  ],
  skills: [
    {
      title: 'Core Expertise',
      description: 'AI-assisted engineering, R/Python, LLM/RAG & MCP, clinical data & bioinformatics, forecasting & stress testing, Excel reporting, Shiny, technical leadership'
    },
    {
      title: 'Programming & Analytics',
      description: 'R, Python, SQL/SQLite, SAS, MATLAB; ggplot2, scikit-learn, Keras/TensorFlow'
    },
    {
      title: 'Applications & Reporting',
      description: 'R Shiny, Streamlit, Tableau, Leaflet, R Markdown, knitr, LaTeX; automated Excel/Word/PDF reporting'
    },
    {
      title: 'AI & Integration',
      description: 'OpenCode, ChatGPT, Model Context Protocol (MCP); Ollama, vLLM, LM Studio, Open WebUI, RAG, n8n, Flowise; human-reviewed AI-assisted development'
    },
    {
      title: 'Engineering',
      description: 'Azure DevOps, GitLab CI/CD, Docker, Camunda, Linux, Microsoft Azure, Google Cloud Platform'
    },
    {
      title: 'Bioinformatics',
      description: 'RNA-seq, microarray, alternative splicing/isoforms, gene-set enrichment, pathway/network analysis, genome assembly, survival analysis; Bioconductor, Cytoscape, GEO, ArrayExpress, Ensembl BioMart, UniProt/Swiss-Prot, GO, Reactome, KEGG, BioGRID, STRING, ChEMBL'
    },
    {
      title: 'Clinical Data',
      description: 'EDC, CRF, edit checks, DMP, data validation plans, UAT, SAE reconciliation; CDISC SDTM/ADaM, RAW/SDTM/ADaM pipelines, QC'
    }
  ],
  positions: [
    {
      period: '2022 - April 2024',
      title: 'Reporting Automation, T-Systems / European Central Bank (ECB)',
      description: [
        'Built an R library for statistical reporting of financial time series; migrated FAME logic to R/Python using Reticulate and automated Word/PDF outputs.',
        'Developed pipelines for automated Excel report generation; integrated analytics with Camunda and supported GitLab CI/CD, unit tests, linting, SonarQube and documentation.',
        'Represented the team lead and contributed to sprint planning, technical coordination, quality assurance and stakeholder-facing delivery.'
      ]
    },
    {
      period: 'June 2025 - December 2025',
      title: 'Data Science & Risk Analytics Consultant, Banking',
      description: [
        'Developed forecasting models with planning horizons of up to five years and implemented stress tests.',
        'Rewrote and refactored legacy R code, optimized four business processes and used human-reviewed AI-assisted development for implementation and documentation.',
        'Built R/Shiny analysis workflows and pipelines for automated Excel report generation; translated business requirements into analytical implementations.'
      ]
    },
    {
      period: 'June 2025 - June 2026',
      title: 'Clinical Data Manager, Pharma / Clinical Studies',
      description: [
        'Managed EDC setup, CRF design, edit checks, user roles, data-transfer specifications and UAT; coordinated with CROs, laboratories and study teams.',
        'Created and reviewed DMPs, validation plans, SAE reconciliation plans and UAT documentation; developed R-based validation workflows and Shiny applications in a GCP-relevant setting.'
      ]
    },
    {
      period: 'July 2026',
      title: 'Clinical Data Pipeline, Dr. Falk Pharma',
      description: [
        'Built a RAW/SDTM/ADaM pipeline in R using OpenCode and AI-assisted development, with validation, QC, traceability and automated Excel-report pipelines.'
      ]
    },
    {
      period: '2026',
      title: 'AI-Assisted Audit-Trail Reporting, Miltenyi Biotec',
      description: [
        'Used OpenCode and AI-assisted development to implement audit-trail reports and automated Excel-report pipelines, including reporting logic, debugging, documentation and human review.'
      ]
    },
    {
      period: 'Since 2019',
      title: 'CEO & Co-Founder / AI & Data Lead, Leap Dynamics GmbH',
      description: [
        'Lead consulting and technical delivery across healthcare, life sciences, finance and AI, combining hands-on implementation with team coordination and stakeholder communication.',
        'Designed LLM/RAG infrastructure and MCP-based tool integration. Established OpenCode-assisted R/Python workflows for code generation, refactoring, testing and documentation with human review.'
      ]
    },
    {
      period: 'Since 2022',
      title: 'Oncology Registry & Biobank, Medical University Pleven',
      description: [
        'Analysed national cancer registry data and built Shiny dashboards for clinical and research stakeholders; supported integration, documentation and biobank workflows across five cancer types.'
      ]
    },
    {
      period: 'Since 2025',
      title: 'Lead Developer, AI Map Europe (ai-map.eu)',
      description: [
        'Built a production-ready R Shiny application; migrated CSV data to SQLite and added admin authentication, institution management, visitor analytics, paid listings and inquiry forms with SMTP notifications.',
        'Improved responsive UI/UX, SEO, structured data and sitemap/robots support; added input validation, output escaping, login throttling and verified-only public records. Audited 400+ institutions using ELLIS, CAIRNE, AI NATION, NET4AI, INSAIT and AI-on-Demand, with provenance and deployment/security documentation.'
      ]
    },
    {
      period: 'Since 2024',
      title: 'LLM-Based Financial News Analytics, Leap Dynamics (internal project)',
      description: [
        'Built recurring sentiment-analysis workflows using LLMs, API data, automated PDF reports, a Streamlit interface, n8n orchestration and Flowise-assisted workflow testing.'
      ]
    },
    {
      period: '2025',
      title: 'Executive-Level Workshops, BVMW',
      description: [
        'Delivered workshops for managing-director-level participants in the BVMW network.'
      ]
    },
    {
      period: '2015 - 2019',
      title: 'Research Associate, Humboldt University / Charité, Berlin',
      description: []
    },
    {
      period: '2015 - 2017',
      title: 'CTO & Co-Founder, Promethano GmbH',
      description: [
        'Life-sciences software and time-series applications.'
      ]
    }
  ]
};
